import { DialogContent, Icon, IconButton } from '@mui/material'
import React, { PropsWithChildren } from 'react'
import { z } from 'zod'

import { Form } from '@/app/component/form'
import { ActionsContainer } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'

import Dialog from './dialog'
import DialogHeader from './dialog-header'
import CustomTooltip from './tooltip'

type Props = PropsWithChildren<{
  disabled?: boolean
  maxLength?: number
  placeholder?: string
  text: string
  title?: string

  onSubmit: (value: string) => Promise<void>
}>

const PopupEditableText: React.FC<Props> = ({
  disabled,
  maxLength = 80,
  placeholder,
  text,
  title = 'Edit',
  children,
  onSubmit,
}) => {
  const [open, setOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  const handleOpen = () => setOpen(true)
  const handleClose = () => (!saving ? setOpen(false) : undefined)

  const schema = React.useMemo(
    () =>
      z.object({
        value: z
          .string()
          .transform((v) => v.trim())
          .pipe(
            z
              .string()
              .min(1, { message: 'Required' })
              .max(maxLength, { message: `Must be at most ${maxLength} characters` }),
          ),
      }),
    [maxLength],
  )

  const defaultValues = React.useMemo(() => ({ value: text }), [text])

  const fields = React.useMemo(
    () =>
      ({
        value: {
          type: 'text',
          fieldProps: {
            name: 'value',
            placeholder,
            required: true,
            autoFocus: true,
          },
        },
      }) as const,
    [placeholder],
  )

  const handleSubmit = React.useCallback(
    async (values: { value: string }) => {
      const next = values.value
      if (next === text) {
        setOpen(false)
        return
      }
      try {
        setSaving(true)
        await onSubmit(next)
        setOpen(false)
      } finally {
        setSaving(false)
      }
    },
    [onSubmit, text],
  )

  return (
    <>
      <div style={{ alignItems: 'center', display: 'flex', width: '100%', gap: 6 }}>
        <div style={{ flexShrink: 1, overflow: 'hidden' }}>{children}</div>
        <CustomTooltip title="Edit">
          <div>
            <IconButton aria-label="edit" disabled={disabled} size="small" onClick={handleOpen}>
              <Icon style={{ fontSize: 16 }}>edit</Icon>
            </IconButton>
          </div>
        </CustomTooltip>
      </div>

      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <DialogHeader icon="edit" title={title} onClose={handleClose} />
        <DialogContent>
          <Form
            defaultValues={defaultValues}
            fields={fields}
            schema={schema}
            shouldReset
            onSubmit={handleSubmit}
            renderActions={({ isDirty }) => (
              <ActionsContainer>
                <SubmitButton isDirty={isDirty} isSubmitting={saving}>
                  Save
                </SubmitButton>
              </ActionsContainer>
            )}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PopupEditableText

export type { Props as PopupEditableTextProps }
